// src/pages/PayNow.tsx
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CreditCard, RefreshCw } from "lucide-react";
import { authFetch, renewLoginIfNeeded } from "../lib/auth";
import "../styles/payment-design.css";

type MyPay = {
  id: number;
  order_id: string;
  title: string | null;
  amount: number;
  status: string;
  pay_url?: string | null;
  created_at: string;
};

export default function PayNow() {
  const [rows, setRows] = useState<MyPay[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string>("");
  const [busyId, setBusyId] = useState<number | null>(null);

  async function load() {
    setLoading(true);
    setErr("");
    try {
      // 1차: 신규 경로
      let r = await authFetch("/api/payments/my/approved");
      if (!r.ok) {
        // 2차: 호환 경로
        r = await authFetch("/api/payments/my?status=approved");
      }
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j?.ok === false) {
        throw new Error(j?.message || `조회 실패 (${r.status})`);
      }
      setRows(j?.items || j?.payments || j?.rows || []);
    } catch (e: any) {
      setErr(e?.message || "승인된 결제 조회에 실패했습니다.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  const fmtAmount = (n: number) => n.toLocaleString("ko-KR");
  const fmtDT = (s: string) => {
    const d = new Date(s);
    return isNaN(+d) ? s : d.toLocaleString("ko-KR");
  };

  // ✅ EzPg 결제창 진입: pay-form 페이지를 새 창으로 열기
  async function startPay(id: number) {
    const popup = window.open("about:blank", "EZPG_PAY_WINDOW", "width=430,height=700,scrollbars=yes");
    try {
      setBusyId(id);

      const token = await renewLoginIfNeeded();
      if (!token) {
        popup?.close();
        alert("로그인이 필요합니다.");
        return;
      }

      const url = `/api/payments/${id}/pay-form?token=${encodeURIComponent(
        token
      )}`;

      if (popup) popup.location.replace(url);

      if (!popup) {
        alert(
          "브라우저에서 팝업이 차단되었습니다.\n" +
            "주소창 오른쪽의 팝업 차단 아이콘을 눌러 팝업을 허용한 뒤 다시 시도해주세요."
        );
      }
    } catch (e: any) {
      popup?.close();
      alert(e?.message || "결제 준비 실패");
    } finally {
      setTimeout(() => setBusyId(null), 800);
    }
  }

  return (
    <main className="jp-payment-list">
      <div className="jp-payment-list-wrap">
      <Link to="/pay" className="jp-pay-button"><ArrowLeft size={15} /> 이용목록</Link>
      <header className="jp-payment-list-heading mt-6">
        <div><h1>결제 가능한 요청</h1><p>승인된 요청을 확인하고 카드로 결제하세요.</p></div>
        <button type="button" className="jp-pay-button" onClick={load} disabled={loading} aria-label="승인된 결제 새로고침">
          <RefreshCw size={16} /> 새로고침
        </button>
      </header>

      {err && (
        <div className="jp-payment-error" role="alert">
          {err}
        </div>
      )}

      {loading && <div className="jp-payment-empty" role="status"><RefreshCw size={24} /><h2>결제 요청을 불러오고 있어요</h2></div>}

      {!loading && rows.length === 0 && !err && (
        <div className="jp-payment-empty"><CreditCard size={28} strokeWidth={1.5} /><h2>결제 가능한 요청이 없습니다</h2><Link to="/pay" className="jp-pay-button">이용목록 보기</Link></div>
      )}

      <div className="jp-payment-items">
      {rows.map((p) => (
        <article key={p.id} className="jp-payment-item is-approved">
          <div className="jp-payment-item-main">
            <div>
              <div className="jp-payment-item-tags"><span className="jp-payment-item-status bg-orange-50 text-orange-700">{p.status === "approved" ? "결제 가능" : p.status}</span></div>
              <h2>
                {p.title || `주문 ${p.order_id}`}
              </h2>
              <div className="jp-payment-item-date">주문번호 {p.order_id}</div>
              <div className="jp-payment-item-date">신청일 {fmtDT(p.created_at)}</div>
            </div>
            <div className="jp-payment-item-amount">
              <span>요청금액</span>
              <strong>{fmtAmount(p.amount)}원</strong>
            </div>
          </div>

          <div className="jp-payment-item-footer">
            <p>카드 결제창에서 결제를 진행해 주세요.</p>
            <div>
            <button
              type="button"
              className="jp-pay-button primary"
              onClick={() => startPay(p.id)}
              disabled={busyId === p.id}
            >
              {busyId === p.id ? "진행 중…" : "결제하기"}
            </button>

            {/* 이미 pay_url이 있는 경우 보조 옵션으로 유지 */}
            {p.pay_url && (
              <a
                className="jp-pay-button"
                href={p.pay_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                링크로 열기
              </a>
            )}
            </div>
          </div>
        </article>
      ))}
      </div>
      </div>
    </main>
  );
}
